import { useState } from "react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { ForgeThreadTarget } from "./forgeExecutionTarget";
import type { SidebarThreadSummary } from "~/types";
import { resolveTaskThread } from "~/lib/taskThreadLink";
import {
  useProjectAutomationsState,
  type ForgeAutomationRunTarget,
} from "../NoActiveThreadState.automations";
import { useProjectTaskBoard } from "../tasks/useProjectTaskBoard";
import { TaskWorkSessionDialog } from "../tasks/TaskWorkSessionDialog";
import { useTaskWorkSessionLauncher } from "../tasks/useTaskWorkSessionLauncher";
import { useCmmdProjectHome } from "./useCmmdProjectHome";
import { useProjectAccessGate } from "./projectAccessGate";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import { CmmdProjectHomeView } from "./CmmdProjectHomeView";
import type { ProjectTaskSelection } from "../tasks/ProjectTaskSurface";
import { useProjectLeadStatus } from "./useProjectLeadStatus";
import { useProjectWorkSessions } from "./useProjectWorkSessions";
import { ProjectWorkItemPrefixUpdateProvider } from "./ProjectWorkItemPrefixContext";
import { useProjectTaskViewRoute } from "./useProjectTaskViewRoute";
import { useProjectRequestedTaskLaunch } from "./useProjectRequestedTaskLaunch";
import type { ProjectTab } from "./CmmdProjectTabs";
import {
  resolveExistingTaskThreadConversation,
  resolveExistingTaskWorkSessionConversation,
  useProjectTab,
} from "./cmmdProjectHomeNavigation";
import { useForgeProjectRemovalContext } from "./useForgeProjectRemovalContext";

export {
  resolveExistingTaskThreadConversation,
  resolveExistingTaskWorkSessionConversation,
} from "./cmmdProjectHomeNavigation";

export interface CmmdProjectHomeProps {
  readonly cmmdProjectId: string;
  /**
   * The tab the URL is asking for, as the ROUTE sees it. Supplied by the route
   * because a router push does not emit `popstate`, so a link straight into
   * `?tab=settings` — the sidebar picker's gear — is invisible to a hook that
   * only watches `window`.
   */
  readonly requestedTab?: ProjectTab | null;
  /**
   * A CMMD desktop handoff link's `task` search parameter, or null when the
   * URL did not ask to open one. See `useProjectRequestedTaskLaunch`.
   */
  readonly requestedTaskId?: string | null;
  /**
   * Clears the route's `task` search param through the router once the
   * handoff link's launch is consumed. See `useProjectRequestedTaskLaunch`.
   */
  readonly onClearRequestedTaskParam?: () => void;
  readonly onOpenForgeThread: (target: ForgeThreadTarget) => void;
  readonly onLeaveProject: () => void;
}

/**
 * Always the choose-or-create flow. There used to be a shortcut here that
 * jumped an already-linked project straight to the execution workspace's own
 * page; that page is gone, and this project's automations render on this page.
 */
function handleOpenAutomationExecution() {
  useCommandPaletteStore.getState().openAddProjectSourceMode("cmmd-project");
}

/**
 * Unified home page for a single CMMD project. Shows overview (metadata +
 * Forge link), tasks (existing planning board), and team (members).
 *
 * On `origin/main` this function already sat at fallow's cognitive budget of
 * 15 (12 hooks, no room left). Wiring in the handoff link's launch adds one
 * more hook call, two more props on `CmmdProjectHomeProps`, and the ternary
 * that forces the Tasks tab open for a requested task, landing at 19. That is
 * hook density and prop count, not new branching, and getting back under 15
 * means restructuring props and hooks this PR did not otherwise need to
 * touch, which is not this PR's scope.
 */
// fallow-ignore-next-line complexity
export function CmmdProjectHome({
  cmmdProjectId,
  requestedTab,
  requestedTaskId = null,
  onClearRequestedTaskParam,
  onOpenForgeThread,
  onLeaveProject,
}: CmmdProjectHomeProps) {
  const {
    project,
    workspace,
    members,
    workspaceMembers,
    workspaceMembersLoading,
    workspaceMembersError,
    agentMembers,
    agentMembersLoading,
    agentMembersError,
    agentEvents,
    agentEventsLoading,
    agentEventsError,
    updates,
    updatesTotal,
    updatesHasMore,
    updatesLoading,
    updatesError,
    forgeTarget,
    executionStatus,
    loading,
    error,
    reload,
    createAgentMember,
    addProjectMember,
    renameAgentMember,
    retireAgentMember,
    updateAgentMemberAvatar,
    listAgentMemberInstances,
    previewAgentMemberConsolidation,
    applyAgentMemberConsolidation,
    updateSettings,
    updateDescription,
    updateWorkItemPrefix,
    updateRepository,
    addUpdate,
    updateUpdate,
    deleteUpdate,
    loadMoreUpdates,
  } = useCmmdProjectHome(cmmdProjectId);
  // A handoff link's task lives on the board, so its notice or its opened
  // detail must land there too, whichever tab the URL otherwise asked for
  // (e.g. `?tab=overview&task=999` still needs the board to show the notice).
  const [activeTab, setActiveTab] = useProjectTab(requestedTaskId ? "tasks" : requestedTab);
  const [taskViewRoute, setTaskViewRoute] = useProjectTaskViewRoute(cmmdProjectId);
  const [taskSelection, setTaskSelection] = useState<ProjectTaskSelection>({ kind: "none" });
  const [selectedAutomationRun, setSelectedAutomationRun] =
    useState<ForgeAutomationRunTarget | null>(null);
  const taskBoard = useProjectTaskBoard(cmmdProjectId, members, agentMembers);
  const workSessions = useProjectWorkSessions(cmmdProjectId);
  const projectLeadStatus = useProjectLeadStatus(cmmdProjectId);
  const automations = useProjectAutomationsState({
    isProjectWorkspace: forgeTarget !== null,
    linkedCmmdProjectId: forgeTarget ? cmmdProjectId : null,
  });

  // F1: live agent status is resolved from sidebar thread summaries scoped to
  // the LINKED Forge environment — not the active one, and never from the
  // numeric CmmdBoardTask.threadId. Summaries live inside each EnvironmentState.
  const { removalTargets: forgeRemovalTargets, threadSummaries: sidebarThreadSummaries } =
    useForgeProjectRemovalContext(forgeTarget, workSessions.allSessions);

  // Resolve the live Forge thread for a task by its CMMD anchor and route to it
  // (fixes the numeric-id navigation bug — never String(numericThreadId)).
  const handleOpenTaskThread = (taskId: number) => {
    const resolved = resolveTaskThread(sidebarThreadSummaries, taskId);
    if (!resolved) return;
    onOpenForgeThread({
      environmentId: resolved.thread.environmentId,
      threadId: resolved.thread.id,
    });
  };
  const handleOpenTaskThreadSummary = (thread: SidebarThreadSummary) => {
    onOpenForgeThread({
      environmentId: thread.environmentId,
      threadId: thread.id,
    });
  };
  const taskWorkSessionLauncher = useTaskWorkSessionLauncher({
    project,
    workspaceName: workspace?.name ?? null,
    tasks: taskBoard.tasks,
    resolveExistingConversation: async (taskId) => {
      const liveThread = resolveExistingTaskThreadConversation(sidebarThreadSummaries, taskId);
      if (liveThread !== null) return liveThread;
      return resolveExistingTaskWorkSessionConversation(await workSessions.reload(), taskId);
    },
    onOpenThread: onOpenForgeThread,
    onSessionStarted: () => {
      void workSessions.reload();
      void taskBoard.reload();
    },
  });

  const handleStartTaskWork = async (task: CmmdBoardTask) => {
    await taskWorkSessionLauncher.openForTask(task);
    return false;
  };
  const { invalidTaskNotice } = useProjectRequestedTaskLaunch({
    requestedTaskId,
    tasks: taskBoard.tasks,
    tasksLoading: taskBoard.loading,
    tasksError: taskBoard.error,
    onSelectTask: setTaskSelection,
    openForTask: taskWorkSessionLauncher.openForTask,
    clearTaskSearchParam: onClearRequestedTaskParam,
  });
  const handleRefreshAutomations = () => {
    void automations.refreshProjectAutomations();
  };

  // Forge's project pages describe a Forge project; a CMMD project without one
  // has nothing for them to describe.
  const access = useProjectAccessGate({ loading, executionStatus, onBlocked: onLeaveProject });
  if (access !== "allowed") return null;

  return (
    <ProjectWorkItemPrefixUpdateProvider onUpdate={updateWorkItemPrefix}>
      <CmmdProjectHomeView
        home={{
          project,
          workspace,
          members,
          workspaceMembers,
          workspaceMembersLoading,
          workspaceMembersError,
          agentMembers,
          agentMembersLoading,
          agentMembersError,
          agentEvents,
          agentEventsLoading,
          agentEventsError,
          updates,
          updatesTotal,
          updatesHasMore,
          updatesLoading,
          updatesError,
          forgeTarget,
          executionStatus,
          loading,
          error,
          reload,
          createAgentMember,
          addProjectMember,
          renameAgentMember,
          retireAgentMember,
          updateAgentMemberAvatar,
          listAgentMemberInstances,
          previewAgentMemberConsolidation,
          applyAgentMemberConsolidation,
          updateSettings,
          updateDescription,
          updateRepository,
          addUpdate,
          updateUpdate,
          deleteUpdate,
          loadMoreUpdates,
        }}
        activeTab={activeTab}
        selectedAutomationRun={selectedAutomationRun}
        taskBoard={taskBoard}
        taskSelection={taskSelection}
        taskViewRoute={taskViewRoute}
        invalidTaskNotice={invalidTaskNotice}
        projectLeadStatus={projectLeadStatus}
        automations={automations}
        workSessions={workSessions}
        taskWorkSessionLauncher={taskWorkSessionLauncher}
        threadSummaries={sidebarThreadSummaries}
        forgeRemovalTargets={forgeRemovalTargets}
        onTabChange={setActiveTab}
        onTaskSelectionChange={setTaskSelection}
        onTaskViewRouteChange={setTaskViewRoute}
        onOpenWorkSessionThread={onOpenForgeThread}
        onOpenTaskThread={handleOpenTaskThread}
        onOpenTaskThreadSummary={handleOpenTaskThreadSummary}
        onStartTaskWork={handleStartTaskWork}
        onOpenAutomationExecution={handleOpenAutomationExecution}
        onSelectAutomationRun={setSelectedAutomationRun}
        onRefreshAutomations={handleRefreshAutomations}
        onLeaveProject={onLeaveProject}
      />
      <TaskWorkSessionDialog launcher={taskWorkSessionLauncher} />
    </ProjectWorkItemPrefixUpdateProvider>
  );
}
